import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { CHANGES_SAVED, useToast } from '../ui/toast';
import {
  FooterSaveRegistryContext,
  FooterSaveStateContext,
  SaveHoldContext,
  type FooterSaveEntry,
  type FooterSaveRegistry,
  type FooterSaveState,
  type FooterSaveStatus,
} from './footerSave';

interface Summary {
  dirty: boolean;
  /** A dirty card cannot save as it stands. */
  blocked: boolean;
  /** A card is saving by its own means. */
  busy: boolean;
}

/**
 * Holds the registrations of the cards on one page, or one tab, for the sticky Save footer (see
 * footerSave.ts). Renders no element of its own.
 *
 * - `active` false: the cards inside are told there is no footer (useFooterSave returns
 *   `inFooter: false`, registers nothing) and keep their own Save. SettingsTabs passes false on a tab
 *   that has no footer. The saves those cards run by their own buttons are still held (useSaveHold),
 *   active or not.
 * - `scope` names what is on show (SettingsTabs passes the tab's key). "All changes saved" belongs to
 *   the scope it was saved in: a new scope starts at "No unsaved changes".
 */
export function FooterSaveProvider({
  children,
  active = true,
  scope = '',
}: {
  children: ReactNode;
  active?: boolean;
  scope?: string;
}) {
  const toast = useToast();
  // Insertion order is registration order: Map.set on a known id keeps its place.
  const entries = useRef(new Map<string, FooterSaveEntry>());
  const [summary, setSummary] = useState<Summary>({ dirty: false, blocked: false, busy: false });
  const [saving, setSaving] = useState(false);
  // The scope of the last save that worked: "All changes saved" shows only in that scope.
  const [savedScope, setSavedScope] = useState<string | null>(null);
  const [shownScope, setShownScope] = useState(scope);
  if (shownScope !== scope) {
    // A new tab: its status starts afresh (React's "adjusting state when a prop changes").
    setShownScope(scope);
    setSavedScope(null);
  }

  const refresh = useCallback(() => {
    let dirty = false;
    let blocked = false;
    let busy = false;
    for (const entry of entries.current.values()) {
      if (entry.dirty) {
        dirty = true;
        if (!entry.canSave) blocked = true;
      }
      if (entry.saving) busy = true;
    }
    setSummary((prev) =>
      prev.dirty === dirty && prev.blocked === blocked && prev.busy === busy ? prev : { dirty, blocked, busy },
    );
  }, []);

  const registry = useMemo<FooterSaveRegistry>(
    () => ({
      set: (entry) => {
        entries.current.set(entry.id, entry);
        refresh();
      },
      remove: (id) => {
        if (entries.current.delete(id)) refresh();
      },
    }),
    [refresh],
  );

  const running = useRef<Promise<boolean> | null>(null);
  const saveAll = useCallback((): Promise<boolean> => {
    if (running.current) return running.current;
    const all = [...entries.current.values()];
    // Lower `order` first. The sort is stable, so cards with the same order keep registration order.
    const dirty = all.filter((entry) => entry.dirty).sort((a, b) => a.order - b.order);
    if (dirty.length === 0) return Promise.resolve(true);
    if (dirty.some((entry) => !entry.canSave) || all.some((entry) => entry.saving)) return Promise.resolve(false);
    const savedIn = scope;
    const run = (async () => {
      setSaving(true);
      let ok = true;
      try {
        for (const entry of dirty) {
          let saved = false;
          try {
            saved = await entry.save();
          } catch {
            saved = false;
          }
          if (!saved) ok = false;
        }
      } finally {
        running.current = null;
        setSaving(false);
      }
      if (ok) {
        setSavedScope(savedIn);
        toast.show(CHANGES_SAVED);
      }
      return ok;
    })();
    running.current = run;
    return run;
  }, [scope, toast]);

  const discardAll = useCallback(() => {
    for (const entry of [...entries.current.values()]) {
      if (entry.dirty) entry.discard();
    }
  }, []);

  // The saves handed to useSaveHold that are still running, each as whether it worked (never rejects).
  const holds = useRef(new Set<Promise<boolean>>());
  const [held, setHeld] = useState(false);
  const hold = useCallback(<T,>(run: Promise<T>): Promise<T> => {
    const outcome = run.then(
      () => true,
      () => false,
    );
    holds.current.add(outcome);
    setHeld(true);
    // Registered before any settleHeld can wait on it, so the set is up to date when that wakes.
    void outcome.then(() => {
      holds.current.delete(outcome);
      if (holds.current.size === 0) setHeld(false);
    });
    return run;
  }, []);
  const settleHeld = useCallback(async (): Promise<boolean> => {
    let ok = true;
    // Again until none is left: a save started while this waited is waited for too.
    while (holds.current.size > 0) {
      const outcomes = await Promise.all([...holds.current]);
      if (outcomes.includes(false)) ok = false;
    }
    return ok;
  }, []);

  const status: FooterSaveStatus = summary.dirty ? 'dirty' : savedScope === scope ? 'saved' : 'clean';
  const state = useMemo<FooterSaveState>(
    () => ({
      dirty: summary.dirty,
      canSave: summary.dirty && !summary.blocked && !summary.busy && !saving,
      saving,
      status,
      saveAll,
      discardAll,
      held,
      settleHeld,
    }),
    [summary, saving, status, saveAll, discardAll, held, settleHeld],
  );

  return (
    <FooterSaveRegistryContext.Provider value={active ? registry : null}>
      <SaveHoldContext.Provider value={hold}>
        <FooterSaveStateContext.Provider value={state}>{children}</FooterSaveStateContext.Provider>
      </SaveHoldContext.Provider>
    </FooterSaveRegistryContext.Provider>
  );
}
